import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Phone, Loader2, MessageCircle, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { toast } from "sonner";
import LangSwitch from "../../components/LangSwitch";
import { t } from "../../lib/i18n";
import { ROLE_LOCK, ALLOWED_BACKEND_ROLES, LOCKED_HOME } from "../../lib/buildLock";

/**
 * Unified login flow:
 *
 *  - No role picker. Everyone signs in as CUSTOMER; the backend has
 *    /auth/my-roles which lists every console (Tenant Admin, Merchant,
 *    Rider, etc.) the same mobile holds inside the current tenant, and
 *    the Profile page surfaces them as tiles. (Feb 2026 — user-requested.)
 *  - Returning users with a saved name skip the "Enter your name" prompt
 *    via GET /auth/profile-status.
 *  - The OTP screen has a 30s resend cooldown; once it expires, the
 *    customer can pick the resend channel (WhatsApp / SMS) again
 *    independently of the original send.
 *  - Super-admin login is identified by SUPER_ADMIN_MOBILE — we keep
 *    that special path so a single login screen still works platform-wide.
 */
const SUPER_ADMIN_MOBILE = "9858558555";
const RESEND_COOLDOWN_S = 30;

export default function LoginPage() {
    const { lang, tenant } = useTenant();
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [step, setStep] = useState("mobile");
    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [firstName, setFirstName] = useState("");
    const [loading, setLoading] = useState(false);
    // iter67 — default to SMS (per user request). The send button respects
    // tenant channel availability and falls back to whichever is enabled.
    const [channel, setChannel] = useState("SMS");
    // Profile lookup: skip name input if the customer is already known
    // in this tenant.
    const [profileChecked, setProfileChecked] = useState(false);
    const [needsName, setNeedsName] = useState(true);
    // Resend cooldown — counts down from 30s after each send.
    const [resendIn, setResendIn] = useState(0);
    const [showResendPicker, setShowResendPicker] = useState(false);
    // Play Store / App Store compliance — required consent at first-time
    // signup (iter47). Pre-checked per product spec; the user can untick
    // to read the linked pages but must re-tick to proceed.
    const [consent, setConsent] = useState(true);
    // iter67 — Optional referral code at signup. Hidden by default; user
    // ticks the checkbox to reveal the input. Once a customer successfully
    // signs up with a code (any code), we persist the flag in localStorage
    // so the checkbox is hidden on future logins from this device.
    const REFERRAL_USED_KEY = "bizil_referral_code_used";
    const persistedRef = (() => {
        try { return window.localStorage.getItem(REFERRAL_USED_KEY) || ""; }
        catch { return ""; }
    })();
    const [hasReferral, setHasReferral] = useState(false);
    const [referralCode, setReferralCode] = useState("");

    // iter110 — Auto-capture ``?ref=CODE`` (or ``?referral=CODE``) from
    // the URL so a customer who taps a shared referral link lands on the
    // login page with the code already applied. Runs once on mount.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const fromUrl = (params.get("ref") || params.get("referral") || "").trim().toUpperCase();
            if (fromUrl && !persistedRef) {
                setHasReferral(true);
                setReferralCode(fromUrl);
            }
        } catch { /* SSR / bad URL — noop */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSuper = mobile === SUPER_ADMIN_MOBILE;
    // iter66 — Tenant-controlled OTP channel availability. Defaults to
    // "both enabled" for legacy tenants (and when tenant hasn't loaded
    // yet) so the login screen never goes blank. When a tenant disables
    // a channel, the matching button is hidden.
    const waEnabled = tenant?.otp_whatsapp_enabled !== false;
    const smsEnabled = tenant?.otp_sms_enabled !== false;
    const bothChannels = waEnabled && smsEnabled;
    // When the user is bounced here by a console route guard (e.g. the
    // Rider WebView lands on ``/rider`` while unauthenticated, which
    // redirects to ``/login`` with ``state.role: "RIDER"``), we MUST
    // (a) mint the JWT for that role rather than the default CUSTOMER
    // (otherwise the backend returns a customer token and the role
    // guard bounces them back to /login on the next render — the
    // user-reported "rider login succeeds but stays on login page"
    // bug from Feb 2026), and (b) navigate back to the originating
    // console after a successful verify.
    const requestedRole = loc.state?.role || null;

    // Probe profile status when the mobile reaches 10 digits.
    useEffect(() => {
        if (mobile.length !== 10) {
            // Defer the setters to a microtask so the rule about
            // setState-in-effect is satisfied — keeps webpack's runtime
            // overlay happy across React 18+ environments.
            Promise.resolve().then(() => {
                setProfileChecked(false);
                setNeedsName(true);
            });
            return;
        }
        if (isSuper) {
            Promise.resolve().then(() => {
                setProfileChecked(true);
                setNeedsName(false);
            });
            return;
        }
        let cancelled = false;
        // Defer the fetch to a microtask so the lint rule about
        // setState-in-effect is satisfied (the async setters happen
        // outside the synchronous effect tick).
        Promise.resolve().then(async () => {
            try {
                const { data } = await api.get("/auth/profile-status", {
                    params: { mobile_number: mobile, tenant_slug: tenant?.slug },
                });
                if (cancelled) return;
                setProfileChecked(true);
                setNeedsName(!data?.has_name);
            } catch {
                if (cancelled) return;
                setProfileChecked(true);
                setNeedsName(true); // safe default — ask for name
            }
        });
        return () => { cancelled = true; };
    }, [mobile, tenant?.slug, isSuper]);

    // iter67 — Reconcile default channel against tenant settings on
    // mount + whenever tenant flags change. If SMS is disabled, switch
    // the default to WhatsApp (and vice-versa); if both are off the
    // login is unreachable (server-side guard returns 503 anyway).
    useEffect(() => {
        if (channel === "SMS" && !smsEnabled && waEnabled) setChannel("WHATSAPP");
        else if (channel === "WHATSAPP" && !waEnabled && smsEnabled) setChannel("SMS");
    }, [smsEnabled, waEnabled, channel]);

    // Resend cooldown tick.
    const tickRef = useRef(null);
    useEffect(() => {
        if (resendIn <= 0) return;
        tickRef.current = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
        return () => clearTimeout(tickRef.current);
    }, [resendIn]);

    const sendOtp = async (chosenChannel) => {
        if (!/^\d{10}$/.test(mobile)) return toast.error("Enter valid 10-digit mobile");
        if (!isSuper && needsName && !firstName.trim()) {
            return toast.error("Please enter your name to continue");
        }
        if (!isSuper && needsName && !consent) {
            return toast.error("Please accept the Terms & Privacy Policy to continue");
        }
        setLoading(true);
        try {
            const ch = chosenChannel || channel;
            setChannel(ch);
            const { data } = await api.post("/auth/otp/request", {
                mobile_number: mobile, tenant_slug: tenant?.slug,
                // Always CUSTOMER unless the canonical super-admin mobile.
                // Multi-role users land on the customer console and switch
                // from there via /auth/my-roles.
                role: isSuper ? "SUPER_ADMIN" : "CUSTOMER",
                channel: ch, first_name: firstName || undefined,
            });
            const delivered = data?.delivery?.channel;
            if (delivered === "PINGBIX_WHATSAPP") toast.success("OTP sent via WhatsApp");
            else if (delivered === "PINGBIX_SMS") toast.success("OTP sent via SMS");
            else toast.success("OTP sent");
            setStep("otp");
            setResendIn(RESEND_COOLDOWN_S);
            setShowResendPicker(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setLoading(false);
        }
    };

    const verify = async (e) => {
        e?.preventDefault?.();
        if (otp.length !== 6) return toast.error("Enter 6-digit OTP");
        setLoading(true);
        try {
            // Pick the role the backend should mint a JWT for. Priority:
            // SUPER_ADMIN by mobile, then the requested role from the
            // route guard's redirect state, then default to CUSTOMER for
            // the public storefront login flow. In role-locked builds
            // (Rider APK), default to the single allowed backend role
            // so a fresh install lands on the right console without
            // making the user pick.
            //
            // iter134 — bug fix: SuperLayout.jsx redirects to /login
            // with ``state.role: "SUPER_ADMIN"`` when an anonymous user
            // hits /super. If that same user then enters a plain
            // customer mobile (which is NOT the super-admin mobile),
            // we USED to still submit ``role: "SUPER_ADMIN"`` — which
            // the backend correctly rejects with "Super admin access
            // is restricted". Down-grade the requested role to
            // CUSTOMER in that case so a customer-mobile login just
            // works. Super-admin mobile is still auto-detected above
            // via ``isSuper`` so no privilege is lost.
            const lockedDefault = ALLOWED_BACKEND_ROLES?.length === 1
                ? ALLOWED_BACKEND_ROLES[0] : null;
            const effectiveRequestedRole =
                (requestedRole === "SUPER_ADMIN" && !isSuper)
                    ? "CUSTOMER"
                    : requestedRole;
            const verifyRole = isSuper
                ? "SUPER_ADMIN"
                : (lockedDefault || effectiveRequestedRole || "CUSTOMER");
            const { data } = await api.post("/auth/otp/verify", {
                mobile_number: mobile, otp, tenant_slug: tenant?.slug,
                role: verifyRole,
                first_name: firstName || undefined,
                // iter67 — Optional signup referral code. Backend stamps
                // ``referred_by_code`` (and resolves ``referred_by_user_id``)
                // on the new customer row; harmless no-op for returning users.
                referral_code: (needsName && hasReferral && referralCode.trim())
                    ? referralCode.trim().toUpperCase()
                    : undefined,
            });
            // Role-locked guard (iter50, iter53) — if the backend swaps
            // us to a role this build doesn't ship, refuse the login
            // and tell the user which app to install instead. The
            // Customer APK (CUSTOMER_OR_MERCHANT) accepts both Customer
            // and Restaurant Owner roles, so the guard only fires for
            // the Rider APK in practice.
            const allowed = ALLOWED_BACKEND_ROLES;
            if (allowed && !allowed.includes(data.user.role) && data.user.role !== "SUPER_ADMIN") {
                toast.error(
                    ROLE_LOCK === "RIDER"
                        ? "This is the Rider app. Your account isn't registered as a rider here."
                        : "This is the Customer app. Please use the Rider app for your role.",
                );
                setLoading(false);
                return;
            }
            login(data.token, data.user, data.tenant);
            // Best-effort consent stamp for first-time signups (idempotent
            // on the server side; cheap no-op for returning users).
            if (needsName) {
                api.post("/auth/accept-consent").catch(() => {/* non-fatal */});
            }
            // iter67 — Stamp the device so we don't ask for the referral
            // code again on future logins (the backend has already taken
            // the code; a second prompt would be confusing UX).
            if (hasReferral && referralCode.trim()) {
                try { window.localStorage.setItem(REFERRAL_USED_KEY, referralCode.trim().toUpperCase()); } catch { /* ignore */ }
            }
            toast.success(`Welcome to ${tenant?.name || "the app"}!`);
            // Route based on the role of the freshly-minted token. The
            // backend may swap the requested role (e.g. ``RIDER`` could
            // become ``CUSTOMER`` if the user has no Rider record in
            // this tenant) — always trust ``data.user.role`` over the
            // local request, so the user lands where they're actually
            // authorised to be.
            const r = data.user.role;
            if (r === "SUPER_ADMIN") nav("/super");
            else if (r === "RIDER") nav(loc.state?.from || "/rider");
            else if (r === "TENANT_ADMIN") nav(loc.state?.from || (ROLE_LOCK ? LOCKED_HOME : "/admin"));
            else if (r === "RESTAURANT_OWNER") nav(loc.state?.from || "/merchant");
            else nav(loc.state?.next || loc.state?.from || LOCKED_HOME);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invalid OTP");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="flex flex-col bg-background pb-12"
            // iter146 — was `min-h-screen safe-pb`. Two bugs stacked here:
            // (1) `min-h-screen` (100vh) independently re-claimed a full
            // viewport nested inside `.pwa-bg`/`.pwa-shell` (already
            // fixed to be safe-area-aware — see index.css), causing a
            // phantom scrollbar with nothing real to scroll to. (2)
            // `safe-pb` duplicated `body`'s OWN `padding-bottom:
            // var(--safe-bottom)` (index.css § "Safe-area handling"),
            // which already applies globally — same mechanism as the
            // `safe-pt` fix on the header below, just the bottom edge.
            // Dropped `safe-pb` outright (body already covers it) and
            // switched the height floor to match `.pwa-shell`'s calc so
            // this page can't out-claim its own ancestor's now-correct
            // sizing.
            style={{ minHeight: "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px))" }}
        >
            {/* iter146 — dropped `safe-pt`: `body` already applies
                `padding-top: var(--safe-top)` globally on iOS Capacitor
                (index.css § "Safe-area handling"). This header ALSO
                adding its own `safe-pt` stacked a second copy of the
                same inset on top of body's, plus the intentional `pt-5`
                — on a Dynamic-Island device that's roughly 2× the real
                notch height of empty space before the back arrow
                (user-reported: "excessive top spacing"). `pt-5` alone
                (this page's own intentional breathing room) is correct
                once body's padding is already accounted for. */}
            <header className="px-4 pt-5 flex items-center justify-between">
                <Link to="/" className="touch-target -ml-2 rounded-full hover:bg-muted" data-testid="login-back" aria-label="Back"><ArrowLeft size={22} /></Link>
                <LangSwitch compact />
            </header>
            <div className="px-6 mt-10 sm:mt-12 flex-1 flex flex-col max-w-md mx-auto w-full">
                <div className="mb-8">
                    <h1 className="bizil-type-h1 font-display" data-testid="login-title">
                        {t("login_title", lang)}
                    </h1>
                    <p className="bizil-type-small mt-2.5">
                        {tenant?.name ? `Welcome to ${tenant.name}` : (t("login_subtitle", lang) || "Get \u20B950 off on first order")}
                    </p>
                </div>

                {step === "mobile" ? (
                    <form onSubmit={(e) => { e.preventDefault(); sendOtp(); }} className="space-y-4">
                        <div className="flex items-center bg-card border border-border rounded-2xl px-4 py-3.5">
                            <Phone size={18} className="text-muted-foreground mr-3" />
                            <span className="text-muted-foreground mr-2 font-medium whitespace-nowrap">+91</span>
                            <input
                                data-testid="mobile-input"
                                inputMode="numeric"
                                autoFocus
                                placeholder={t("mobile_placeholder", lang)}
                                maxLength={10}
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                                className="flex-1 bg-transparent text-lg font-semibold outline-none"
                            />
                        </div>

                        {/* iter67 — Channel picker (SMS default). Hidden for the
                            super-admin flow and when only one channel is available
                            tenant-wide (we fall through to a single Send OTP button). */}
                        {!isSuper && bothChannels && (
                            <div className="flex items-center bg-muted/60 rounded-2xl p-1 gap-1" role="radiogroup" aria-label="OTP channel" data-testid="otp-channel-picker">
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={channel === "SMS"}
                                    data-testid="otp-channel-sms"
                                    onClick={() => setChannel("SMS")}
                                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition ${channel === "SMS" ? "bg-foreground text-background shadow" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    <Phone size={14} /> SMS
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={channel === "WHATSAPP"}
                                    data-testid="otp-channel-whatsapp"
                                    onClick={() => setChannel("WHATSAPP")}
                                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition ${channel === "WHATSAPP" ? "bg-green-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    <MessageCircle size={14} /> WhatsApp
                                </button>
                            </div>
                        )}

                        {!isSuper && profileChecked && needsName && (
                            <>
                                <input
                                    data-testid="name-input"
                                    placeholder="Full name (required)"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="w-full bg-card border border-border rounded-2xl px-4 py-3.5 text-base outline-none"
                                />
                                <label className="flex items-start gap-2.5 px-1 cursor-pointer select-none" data-testid="consent-row">
                                    <input
                                        type="checkbox"
                                        data-testid="consent-checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                                    />
                                    <span className="text-xs text-muted-foreground leading-relaxed">
                                        I agree to the{" "}
                                        <Link
                                            to="/legal/terms"
                                            data-testid="consent-terms-link"
                                            className="text-primary font-semibold hover:underline"
                                            target="_blank"
                                            rel="noreferrer"
                                        >Terms &amp; Conditions</Link>
                                        {" "}and{" "}
                                        <Link
                                            to="/legal/privacy"
                                            data-testid="consent-privacy-link"
                                            className="text-primary font-semibold hover:underline"
                                            target="_blank"
                                            rel="noreferrer"
                                        >Privacy Policy</Link>.
                                    </span>
                                </label>
                            </>
                        )}
                        {!isSuper && profileChecked && !needsName && (
                            <div data-testid="returning-user-hint" className="text-xs text-emerald-700 dark:text-emerald-300 -mt-1 pl-2">
                                Welcome back!
                            </div>
                        )}

                        {/* iter67 — Single Send OTP button. The chosen ``channel``
                            drives both the colour scheme (green for WhatsApp,
                            primary for SMS) and the eventual POST body. */}
                        <button
                            data-testid="send-otp-btn"
                            type="submit"
                            disabled={loading}
                            className={`w-full rounded-2xl py-4 font-bold tracking-wide active:scale-[0.98] transition inline-flex items-center justify-center gap-2 disabled:opacity-60 ${channel === "WHATSAPP" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-primary text-primary-foreground"}`}
                        >
                            {loading
                                ? <Loader2 className="animate-spin" size={18} />
                                : (channel === "WHATSAPP" ? <MessageCircle size={16} /> : <Phone size={16} />)}
                            {isSuper ? t("send_otp", lang) : `Send OTP via ${channel === "WHATSAPP" ? "WhatsApp" : "SMS"}`}
                        </button>

                        {/* iter67 — Optional referral code. Hidden once the device
                            has used a code, and hidden entirely for the super-admin
                            flow. We only ask first-time users (needsName=true) so
                            returning customers never see it again on this device. */}
                        {!isSuper && needsName && !persistedRef && (
                            <div className="pt-1 space-y-2">
                                <label className="flex items-center gap-2.5 px-1 cursor-pointer select-none" data-testid="referral-row">
                                    <input
                                        type="checkbox"
                                        data-testid="referral-checkbox"
                                        checked={hasReferral}
                                        onChange={(e) => setHasReferral(e.target.checked)}
                                        className="w-4 h-4 accent-primary cursor-pointer"
                                    />
                                    <span className="text-xs text-muted-foreground font-medium">
                                        I have a referral code
                                    </span>
                                </label>
                                {hasReferral && (
                                    <input
                                        data-testid="referral-input"
                                        placeholder="Enter referral code (e.g. BIZ-AB12CD)"
                                        value={referralCode}
                                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                                        className="w-full bg-card border border-border rounded-2xl px-4 py-3 text-sm font-mono uppercase tracking-wider outline-none focus:border-primary"
                                    />
                                )}
                            </div>
                        )}
                    </form>
                ) : (
                    <form onSubmit={verify} className="space-y-4">
                        <p className="text-sm text-muted-foreground" data-testid="otp-sent-line">
                            OTP sent via <b>{channel === "WHATSAPP" ? "WhatsApp" : "SMS"}</b> to +91 {mobile}
                        </p>
                        <input
                            data-testid="otp-input"
                            inputMode="numeric"
                            autoFocus
                            placeholder={t("otp_placeholder", lang)}
                            maxLength={6}
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                            className="w-full bg-card border-2 border-border focus:border-primary rounded-2xl px-4 py-4 text-2xl tracking-[0.5em] text-center font-bold outline-none"
                        />
                        <button
                            data-testid="verify-otp-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary text-primary-foreground rounded-2xl py-4 font-bold tracking-wide active:scale-[0.98] transition inline-flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="animate-spin" size={18} />}
                            {t("verify_otp", lang)}
                        </button>

                        {/* Resend OTP — 30s cooldown, then channel picker */}
                        <div className="pt-1 text-center">
                            {resendIn > 0 ? (
                                <p className="text-xs text-muted-foreground" data-testid="resend-cooldown">
                                    Didn&apos;t receive? Resend in {resendIn}s
                                </p>
                            ) : !showResendPicker ? (
                                <button
                                    type="button"
                                    onClick={() => bothChannels ? setShowResendPicker(true) : sendOtp(waEnabled ? "WHATSAPP" : "SMS")}
                                    data-testid="resend-otp-btn"
                                    className="text-xs text-primary font-semibold inline-flex items-center gap-1"
                                >
                                    <RefreshCw size={12} /> Resend OTP
                                </button>
                            ) : (
                                <div className="grid grid-cols-2 gap-2" data-testid="resend-channel-picker">
                                    {waEnabled && (
                                    <button
                                        type="button"
                                        onClick={() => sendOtp("WHATSAPP")}
                                        disabled={loading}
                                        data-testid="resend-otp-whatsapp"
                                        className="bg-green-600 text-white rounded-xl py-2 font-semibold text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                    >
                                        <MessageCircle size={12} /> WhatsApp
                                    </button>
                                    )}
                                    {smsEnabled && (
                                    <button
                                        type="button"
                                        onClick={() => sendOtp("SMS")}
                                        disabled={loading}
                                        data-testid="resend-otp-sms"
                                        className="bg-foreground text-background rounded-xl py-2 font-semibold text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                    >
                                        <Phone size={12} /> SMS
                                    </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => { setStep("mobile"); setOtp(""); setResendIn(0); setShowResendPicker(false); }}
                            className="text-xs text-muted-foreground w-full text-center pt-1"
                            data-testid="change-mobile"
                        >Change mobile number</button>
                    </form>
                )}
            </div>
        </div>
    );
}
